
/**
 * @file Keycloak service for authentication and user profile management.
 * @module services/keycloakService
 */

import Keycloak from "keycloak-js";
import logger from "@/utils/logger";
import { KEYCLOAK_ROLES } from '@/utils/constants.js';

/**
 * Keycloak service options.
 * @typedef {Object} KeycloakOptions
 * @property {string} url - The Keycloak server URL.
 * @property {string} clientId - The Keycloak client ID.
 * @property {string} realm - The Keycloak realm.
 */

const options = {
    url: import.meta.env.VITE_KEYCLOAK_URL,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
    realm: import.meta.env.VITE_KEYCLOAK_REALM
};

const keycloak = new Keycloak(options);
let store = null;

async function sendSessionTerminationEmailSafely(email) {
    try {
        const { sendSessionTerminationEmail } = await import('@/services/emailService.js');
        await sendSessionTerminationEmail(email);
    } catch (error) {
        logger.error('Failed to send session termination email', error);
    }
}

/**
 * Initializes the Keycloak service.
 * @async
 * @function init
 * @returns {Promise<void>} A promise that resolves when Keycloak is initialized successfully.
 */
// Use a flag to avoid sending the email multiple times when the user session times out.
let sessionExpiredHandled = false;
async function init() {
    try {
        await keycloak.init({ onLoad: 'check-sso', checkLoginIFrame: false });

        // Set up token refresh to check if the user session times out
        setInterval(async () => {
            if (keycloak.authenticated) {
                try {
                    const refreshed = await keycloak.updateToken(60); // refresh if expiring within 60s
                    if (refreshed) {
                        logger.info('Token successfully refreshed');
                    }
                } catch (err) {
                    logger.error('Session may have expired', err);

                    // Handle session expiration
                    const email = keycloak.tokenParsed?.email;
                    if (email) {
                        if (!sessionExpiredHandled) {
                            sessionExpiredHandled = true;
                            await sendSessionTerminationEmailSafely(email);
                        }
                    }

                    // User session times out, redirect to home page.
                    await keycloak.logout();
                }
            }
        }, 30000); // Check every 30 seconds
    } catch (error) {
        logger.error('Keycloak init failed', error);
    }
}

/**
 * Initializes the Keycloak store.
 * @async
 * @function initStore
 * @param {Object} storeInstance - The store instance.
 * @returns {Promise<void>} A promise that resolves when the store is initialized successfully.
 */
async function initStore(storeInstance) {
    try {
        store = storeInstance;
        await store.initOauth(keycloak);
    } catch (error) {
        logger.error('Keycloak init failed', error);
    }
}

/**
 * Retrieves the user profile from Keycloak.
 * @async
 * @function getProfile
 * @returns {Promise<Object|null>} A promise that resolves with the user profile if the user is authenticated, otherwise null.
 */
/**
 * Helper function to check if a user has a specific role.
 * @function hasRole
 * @param {Array<string>} roles - Array of user roles.
 * @param {string} role - Role to check for.
 * @returns {boolean} True if the user has the role, false otherwise.
 */
function hasRole(roles, role) {
    return roles.includes(role);
}

async function getProfile() {
    try {
        if (keycloak.authenticated) {
            await keycloak.loadUserProfile();
            const username = `${keycloak.profile.username}`;
            const fullName = `${keycloak.profile.firstName} ${keycloak.profile.lastName}`;
            const initials = `${keycloak.profile.firstName.charAt(0)}${keycloak.profile.lastName.charAt(0)}`.toUpperCase();
            const email = `${keycloak.profile.email}`;
            const roles = keycloak.resourceAccess[import.meta.env.VITE_KEYCLOAK_CLIENT_ID].roles || [];
            const isStaffUser = email.toLowerCase().includes('@state.mn.us');

            return {
                ...keycloak.profile,
                username: username,
                fullName: fullName,
                email: email,
                initials: initials,
                roles: roles,
                isAdminUser: hasRole(roles, KEYCLOAK_ROLES.ADMIN),
                isStaffUser: isStaffUser,
                isNetworkAdequacyUser: hasRole(roles, KEYCLOAK_ROLES.NetworkAdequacy),
                isQAUser: hasRole(roles, KEYCLOAK_ROLES.QA),
                isInvestigatorUser: hasRole(roles, KEYCLOAK_ROLES.Investigator),
                isQAHMOUser: hasRole(roles, KEYCLOAK_ROLES.QA_HMO),
                isQADHSUser: hasRole(roles, KEYCLOAK_ROLES.QA_DHS),
                isNetAdExternalUser: hasRole(roles, KEYCLOAK_ROLES.NetAdExternal),
                isHMOReportingUser: hasRole(roles, KEYCLOAK_ROLES.HMOReporting),
                isHMOLicensingUser: hasRole(roles, KEYCLOAK_ROLES.HMOLicensing),
                isPBMUser: hasRole(roles, KEYCLOAK_ROLES.PBM),
                isEntityAdministratorUser: hasRole(roles, KEYCLOAK_ROLES.EntityAdministrator)
            };
        } else {
            return null;
        }
    } catch (error) {
        logger.error('Keycloak getUserProfile failed', error);
    }
}

/**
 * Logs out the user from Keycloak.
 * @async
 * @function logout
 * @returns {Promise<void>} A promise that resolves when the user is logged out successfully.
 */
async function logout() {
    try {
        // when user log out, send an email with the submitted documents info
        if (keycloak.authenticated) {
            const email = keycloak.tokenParsed?.email;
            if (email) {
                await sendSessionTerminationEmailSafely(email);
            }
        }
        await keycloak.logout();
    } catch (error) {
        logger.error('Keycloak logout failed', error);
    }
}

async function login(redirectUri) {
    const options = {};
    if (redirectUri) {
        options.redirectUri = redirectUri;
    } else {
        options.redirectUri = window.location.origin;
    }

    await keycloak.login(options);
}

async function refreshToken() {
    try {
        if (await keycloak.updateToken(300)) {
            return keycloak;
        } else {
            return null;
        }
    } catch (error) {
        logger.error('Keycloak refreshToken failed', error);
    }
}


/**
 * Keycloak service object.
 * @namespace KeycloakService
 */
const KeycloakService = {
    CallInit: init,
    CallInitStore: initStore,
    CallLogin: login,
    CallLogout: logout,
    CallTokenRefresh: refreshToken,
    GetUserProfile: getProfile,
};

export default KeycloakService;